import { Parser } from '~/api';
import { ServerTemplate } from '~/api/models';
import RequestService from './request';

interface ResyncPreview {
    total: number;
    diverged: number;
    eligible: number;
    skipped: number;
}

interface ResyncResult {
    dispatched: number;
}

interface ImportServerTemplateFileRequest {
    import_file: File;
    egg_id?: string;
}

interface ImportEggResolution {
    name: string | null;
    egg_resolved: boolean;
    egg: { id: string; name: string } | null;
}

class ServerTemplatesService {
    getAll(req: PaginatableRequest, extraData = {}): Promise<ListResponse> {
        return RequestService.get('/server-templates', {
            ...req,
            ...extraData,
            include: ['serversCount'],
        }).then(Parser.parse);
    }

    get(): Promise<ServerTemplate> {
        return RequestService.get('/server-templates/:serverTemplate', {
            include: ['variables', 'egg', 'schedules', 'tags'],
        }).then(Parser.parse);
    }

    getById(data: { id: string }): Promise<ServerTemplate> {
        return RequestService.get(`/server-templates/${data.id}`, {
            include: ['variables', 'egg', 'tags'],
        }).then(Parser.parse);
    }

    create(data: Record<string, unknown>): Promise<ServerTemplate> {
        return RequestService.post('/server-templates', data).then(Parser.parse);
    }

    update(data: Record<string, unknown>): Promise<ServerTemplate> {
        return RequestService.put('/server-templates/:serverTemplate', data).then(Parser.parse).then(RequestService.updateModelBinding);
    }

    duplicate(data: { id: string }): Promise<ServerTemplate> {
        return RequestService.post(`/server-templates/${data.id}/duplicate`).then(Parser.parse);
    }

    // Validate an uploaded template file and report whether its egg auto-resolves,
    // so the UI can prompt for an egg only when needed.
    importPreview(data: { import_file: File }): Promise<ImportEggResolution> {
        const formData = new FormData();
        formData.append('import_file', data.import_file);

        return RequestService.post('/server-templates/import-file/preview', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }).then((res) => res.data);
    }

    importFile(data: ImportServerTemplateFileRequest): Promise<ServerTemplate> {
        const formData = new FormData();
        formData.append('import_file', data.import_file);
        if (data.egg_id) {
            formData.append('egg_id', data.egg_id);
        }

        return RequestService.post('/server-templates/import-file', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }).then(Parser.parse);
    }

    // Fetch the raw export JSON for a template (used by the diff/compare view).
    // Requested as text so the canonical formatting from the backend is preserved.
    exportJson(data: { id: string }): Promise<string> {
        return RequestService.get(`/server-templates/${data.id}/export`, undefined, { responseType: 'text' });
    }

    exportUrl(id: string): string {
        return `${Wisp.BaseURL}/api/admin/server-templates/${id}/export`;
    }

    createFromServer(data: { name: string; attach: boolean }): Promise<ServerTemplate> {
        return RequestService.post('/servers/:server/create-template', {
            name: data.name,
            attach: data.attach,
        }).then(Parser.parse);
    }

    delete(data: { id: string }): Promise<void> {
        return RequestService.delete(`/server-templates/${data.id}`);
    }

    resyncPreview(data: { id: string }): Promise<ResyncPreview> {
        return RequestService.get(`/server-templates/${data.id}/resync`).then((res) => res.data);
    }

    resync(data: { id: string }): Promise<ResyncResult> {
        return RequestService.post(`/server-templates/${data.id}/resync`).then((res) => res.data);
    }
}

export default new ServerTemplatesService();
